<script setup>
// Config do nó Switch — casos dinâmicos (cada caso = uma saída) + fallback opcional.
// Espelha SwitchConfig do original. Cada caso: { label, field, operator, values[] }.
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

const d = computed(() => props.node.data);

const FIELDS = computed(() => [
  { value: 'message', label: t('CHATBOT.nodeConfig.switch.field_message') },
  {
    value: 'senderName',
    label: t('CHATBOT.nodeConfig.switch.field_senderName'),
  },
  {
    value: 'contactEmail',
    label: t('CHATBOT.nodeConfig.switch.field_contactEmail'),
  },
  { value: 'inboxId', label: t('CHATBOT.nodeConfig.switch.field_inboxId') },
  { value: 'response', label: t('CHATBOT.nodeConfig.switch.field_response') },
]);
const OPERATORS = computed(() => [
  { value: 'contains', label: t('CHATBOT.nodeConfig.switch.op_contains') },
  { value: 'equals', label: t('CHATBOT.nodeConfig.switch.op_equals') },
  { value: 'notEquals', label: t('CHATBOT.nodeConfig.switch.op_notEquals') },
  { value: 'startsWith', label: t('CHATBOT.nodeConfig.switch.op_startsWith') },
  { value: 'endsWith', label: t('CHATBOT.nodeConfig.switch.op_endsWith') },
  {
    value: 'notContains',
    label: t('CHATBOT.nodeConfig.switch.op_notContains'),
  },
  {
    value: 'greaterThan',
    label: t('CHATBOT.nodeConfig.switch.op_greaterThan'),
  },
  { value: 'lessThan', label: t('CHATBOT.nodeConfig.switch.op_lessThan') },
]);

const cases = computed(() => d.value.cases || []);
const addCase = () => {
  if (!d.value.cases) d.value.cases = [];
  d.value.cases.push({
    label: t('CHATBOT.nodeConfig.switch.caseTitle', {
      n: d.value.cases.length + 1,
    }),
    field: 'message',
    operator: 'contains',
    values: [],
  });
};
const removeCase = i => d.value.cases.splice(i, 1);
const valuesText = c => (c.values || []).join(', ');
const setValues = (c, txt) => {
  c.values = txt
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

onMounted(() => {
  if (!d.value.cases) d.value.cases = [];
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        t('CHATBOT.nodeConfig.switch.casesLabel')
      }}</label>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="addCase"
      >
        {{ t('CHATBOT.nodeConfig.switch.addCase') }}
      </button>
    </div>
    <p v-if="!cases.length" class="mb-2 text-xs text-n-slate-10 mt-1">
      {{ t('CHATBOT.nodeConfig.switch.noCases') }}
    </p>

    <div
      v-for="(c, i) in cases"
      :key="i"
      class="p-2 mb-2 border rounded-md border-n-weak"
    >
      <div class="flex items-center justify-between mb-1">
        <span class="text-[0.6875rem] font-medium text-n-slate-11">{{
          t('CHATBOT.nodeConfig.switch.caseTitle', { n: i + 1 })
        }}</span>
        <button class="text-xs text-n-ruby-11" @click="removeCase(i)">
          {{ t('CHATBOT.nodeConfig.switch.removeCase') }}
        </button>
      </div>
      <FlowField
        :model-value="c.label"
        type="text"
        :placeholder="t('CHATBOT.nodeConfig.switch.caseNameLabel')"
        @update:model-value="c.label = $event"
      />
      <div class="flex gap-1">
        <FlowField
          class="flex-1"
          :model-value="c.field"
          type="select"
          :options="FIELDS"
          @update:model-value="c.field = $event"
        />
        <FlowField
          class="flex-1"
          :model-value="c.operator"
          type="select"
          :options="OPERATORS"
          @update:model-value="c.operator = $event"
        />
      </div>
      <FlowField
        :model-value="valuesText(c)"
        type="text"
        :placeholder="t('CHATBOT.nodeConfig.switch.valuesLabel')"
        @update:model-value="setValues(c, $event)"
      />
    </div>

    <FlowField
      :model-value="!!d.fallback"
      :label="t('CHATBOT.nodeConfig.switch.fallbackLabel')"
      type="checkbox"
      @update:model-value="d.fallback = $event"
    />
  </div>
</template>
